import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { clsx, type ClassValue } from "clsx";
import moment from "moment";
import { useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// for getting required fields
const isOptional = (schema: any): boolean => {
  if (schema instanceof z.ZodOptional) return true;
  if (schema instanceof z.ZodDefault) return true;
  return false;
};

// Custom hook to check if user has a given role
export const useHasRole = (role: string) => {
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const roles = loggedInUserData?.role || [];

  return Array.isArray(roles) && roles.includes(role);
};

const unwrapOptional = (schema: any) => {
  if (schema instanceof z.ZodOptional) {
    return unwrapOptional(schema._def.innerType);
  }
  if (schema instanceof z.ZodDefault) {
    return unwrapOptional(schema._def.innerType);
  }
  return schema;
};

export const toYMD = (value: any) => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const getRequiredFields = (schema: any, prefix = ""): string[] => {
  if (isOptional(schema)) return [];

  schema = unwrapOptional(schema);

  if (schema instanceof z.ZodObject) {
    return Object.entries(schema.shape).flatMap(([key, value]) => {
      const fieldName = prefix ? `${prefix}.${key}` : key;
      return getRequiredFields(value, fieldName);
    });
  }

  if (schema instanceof z.ZodArray) {
    const hasMinConstraint = schema._def.minLength !== null;
    const innerRequired = getRequiredFields(schema._def.type, `${prefix}[]`);
    return hasMinConstraint ? [prefix, ...innerRequired] : innerRequired;
  }

  if (schema instanceof z.ZodEffects) {
    const inner = schema._def.schema;
    const innerRequired = getRequiredFields(inner, prefix);

    const isRefinement = schema._def.effect?.type === "refinement";
    return isRefinement ? [prefix, ...innerRequired] : innerRequired;
  }

  if (
    schema instanceof z.ZodString ||
    schema instanceof z.ZodNumber ||
    schema instanceof z.ZodDate
  ) {
    return [prefix];
  }

  return [];
};

// ------------------------------

export const removeEmptyFields = (data: any) => {
  return Object.fromEntries(
    Object.entries(data).filter(
      ([_, value]) => value !== undefined && value !== "" && value !== null
    )
  );
};

export const setEmptyFieldsToNull = (data: any) => {
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (typeof value === "string" && value.trim() === "") {
      data[key] = null;
    }
  });
  return data;
};

export const sortByDate = (data: any, key: string) => {
  const a = new Date(data[key]).getTime();
  const b = new Date(data[key]).getTime();
  return a < b ? -1 : a > b ? 1 : 0;
};

export const formatAmount = (
  amount: number | string,
  format: "currency" | "decimal" = "currency",
  maximumFractionDigits: number = 0,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "$0";

  if (format == "decimal") {
    return new Intl.NumberFormat(locale, {
      style: "decimal",
      maximumFractionDigits: maximumFractionDigits,
    }).format(numericAmount);
  } else {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: maximumFractionDigits,
    }).format(numericAmount);
  }
};

export const yearOptions = (start: number = 2023, end: number = 2050) => {
  return Array.from({ length: end - start + 1 }, (_, i) => {
    const year = start + i;
    return {
      label: year.toString(),
      value: year.toString(),
    };
  });
};

export const yearRangeOptions = (start: number = 2023, end: number = 2050) => {
  return Array.from({ length: end - start }, (_, i) => {
    const from = start + i;
    const to = from + 1;

    return {
      label: `${from} - ${to}`,
      value: `${from}-${to}`,
    };
  });
};

export const monthOptions = [
  { label: "All", value: "all" },
  ...moment.months().map((month, index) => ({
    label: month,
    value: (index + 1).toString().padStart(2, "0"),
  })),
];

export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatTwoDigits = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};

export const formatCurrencyShort = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value}`;
};

export const calculatePercentageChange = (
  current: number,
  previous: number
): { value: string; progress: string } => {
  if (previous == 0) return { value: "0", progress: "" };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? "+" : "";

  return {
    value: change ? `${sign}${(change || 0).toFixed(2)}%` : "0",
    progress: change ? (change >= 0 ? "up" : "down") : "",
  };
};

export const status = {
  active: { color: "text-green", bgColor: "bg-green-50" },
  inactive: { color: "text-primary", bgColor: "bg-primary-50" },
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const taskStatus = {
  Completed: {
    label: "Completed",
    value: "Completed",
    bgColor: "bg-green-50",
    color: "text-green",
  },
  "On hold": {
    label: "On hold",
    value: "On hold",
    bgColor: "bg-red-50",
    color: "text-red-600",
  },
  "In-progress": {
    label: "In Progress",
    value: "In-progress",
    color: "text-primary",
    bgColor: "bg-primary-50",
  },
  "To-Do": {
    label: "To Do",
    value: "To-Do",
    bgColor: "bg-blue-50",
    color: "text-blue-600", // fixed typo (was bg-blue-600)
  },
};

export const reportStatus = {
  "Lab Done": {
    label: "Lab Done",
    value: "Lab Done",
    bgColor: "bg-green-50",
    color: "text-green",
  },
  "Delayed": {
    label: "Delayed",
    value: "Delayed",
    bgColor: "bg-red-50",
    color: "text-red-600",
  },
  "On Track": {
    label: "On Track",
    value: "On Track",
    color: "text-primary",
    bgColor: "bg-primary-50",
  },
  "To-Do": {
    label: "To Do",
    value: "To-Do",
    bgColor: "bg-blue-50",
    color: "text-blue-600", // fixed typo (was bg-blue-600)
  },
  "Stocking Done": {
    label: "Stocking Done",
    value: "Stocking Done",
    bgColor: "bg-green-50",
    color: "text-green-600", // fixed typo (was bg-blue-600)
  },
};

export const reportCrmStatus = {
  "Yes": {
    label: "Yes",
    value: "true",
    bgColor: "bg-green-50",
    color: "text-green",
  },
  "No": {
    label: "No",
    value: "false",
    bgColor: "bg-red-50",
    color: "text-red-600",
  },
};

export const LOGIN_ROLES = {
  SUPER_ADMIN: "SUPER_ADMIN",
  PRODUCT_TEAM: "PRODUCT_TEAM",
  SCHOOL_ADMIN: "SCHOOL_ADMIN",
  STUDENT: "STUDENT",
  CUSTOMER: "CUSTOMER",
  B2C_FULLFILLMENT: "B2C_FULLFILLMENT",
  CRM_HEAD: "CRM_HEAD",
  REGION_HEAD: "REGION_HEAD",
  RELATIONSHIP_MANAGER: "RELATIONSHIP_MANAGER",
  FINANCE_TEAM: "FINANCE_TEAM",
  INVENTORY_TEAM: "INVENTORY_TEAM",
  LABS_TEAM: "LABS_TEAM",
  SALES_OPS: "SALES_OPS",
  LABS_TEAM_HEAD: "LABS_TEAM_HEAD",
} as const;

const ROLE_PATH_MAP = new Map<string, string>([
  [LOGIN_ROLES.SUPER_ADMIN, routes.admin],
  [LOGIN_ROLES.CRM_HEAD, routes.crm_head],
  [LOGIN_ROLES.SALES_OPS, routes.salesOps],
  [LOGIN_ROLES.FINANCE_TEAM, routes.finance_team],
  [LOGIN_ROLES.LABS_TEAM_HEAD, routes.lab_team_head],
  [LOGIN_ROLES.LABS_TEAM, routes.lab_team],
  [LOGIN_ROLES.INVENTORY_TEAM, routes.inventory_team],
  [LOGIN_ROLES.REGION_HEAD, routes.region_head],
  [LOGIN_ROLES.RELATIONSHIP_MANAGER, routes.relationship_manager],
]);

export const getBasePath = (
  userRole: string[] | string | undefined
): string => {
  if (!userRole) {
    return "/finance"; // Default fallback
  }

  const roles = Array.isArray(userRole) ? userRole : [userRole];

  for (const role of roles) {
    const path = ROLE_PATH_MAP.get(role);
    if (path) {
      return path;
    }
  }

  return "/finance";
};

// Utility to convert number → Roman numeral
const toRoman = (num: number): string => {
  const romanMap: [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];

  let result = "";
  for (const [value, numeral] of romanMap) {
    while (num >= value) {
      result += numeral;
      num -= value;
    }
  }
  return result;
};

// Generate options with Roman numerals
export const getRomanOptions = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, i) => {
    const number = start + i;
    return {
      label: toRoman(number),
      value: number.toString(),
    };
  });
};

export const getDayOptions = () => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return days.map((day) => ({
    label: day,
    value: day,
  }));
};
